import React, {useState, useEffect, useCallback, useRef} from 'react';
import {useTranslation} from 'react-i18next';
import SMCDayPickerInput from '../../../components/Simple/SMCDayPickerInput';
import FilterLayout from '../FilterLayout';
import RowLayout from '../../../components/Layouts/RowLayout';
import SMCExpansionPanel from '../../../components/Simple/SMCExpansionPanel';
import SMCIcon from '../../../components/Simple/SMCIcon';
import {SMCSelect, SMCOption} from '../../../components/Simple/SMCSelect';
import Api from '../../../api/SMPFetchHelper';
import {SessionDataGetSchema} from '../../../api/DBSchema/SessionData';
import {SystemUserFilterSchema} from '../../../api/DBSchema/SystemUser';


function DateRangeChooser({
    fromDate,
    toDate,
    setFromDate,
    setToDate
}) {
    return (
        <>
        <RowLayout spacing="4px">
            <span style={{ width: 50 }}>From:</span>
            <SMCDayPickerInput
                placeholder="From..."
                value={fromDate}
                onChange={setFromDate}
                maxDate={toDate}
                width={85}
                noFuture={true}
            />
            <SMCIcon icon="clear" onClick={() => setFromDate(null)} />
        </RowLayout>
        <RowLayout spacing="4px">
            <span style={{ width: 50 }}>To:</span>
            <SMCDayPickerInput
                placeholder="To..."
                value={toDate}
                onChange={setToDate}
                minDate={fromDate}
                width={85}
                noFuture={true}
            />
            <SMCIcon icon="clear" onClick={() => setToDate(null)} />
        </RowLayout>
        </>
    );
}

function SystemUserFilterPane({
    initialFilter,
    onFilterChange,
    open
}) {
    const { t } = useTranslation();
    const didMountRef = useRef(true);
    const freeTextSearchRef = useRef();

    useEffect(() => {
        if (open && freeTextSearchRef.current) {
            freeTextSearchRef.current.focus();
        }
    }, [open]);

    const [availableRoles, setAvailableRoles] = useState(null);
    const [pageLoaded, setPageLoaded] = useState(false);
    const [/*pageError*/, setPageError] = useState(false);

    const [role, setRole] = useState('');

    const [freeTextSearch, setFreeTextSearch] = useState(
        initialFilter.freeTextSearch ? initialFilter.freeTextSearch.value : ''
    );
        
    const [creationFromDate, setCreationFromDate] = useState(null);
    const [creationToDate, setCreationToDate] = useState(null);

    const [modificationFromDate, setModificationFromDate] = useState(null);
    const [modificationToDate, setModificationToDate] = useState(null);
    

    const resetToDefaults = useCallback(() => {
        setFreeTextSearch('');
        
        setRole('');

        setCreationFromDate(null);
        setCreationToDate(null);
        
        setModificationFromDate(null);
        setModificationToDate(null);
    }, []);


    // Update filter request object on value change
    useEffect(() => {
        // Don't run on first render
        if (didMountRef.current === true) {
            didMountRef.current = false;
            return;
        }

        const filterRequestObj = {};
        // Free text search
        if (freeTextSearch) {
            filterRequestObj[SystemUserFilterSchema.freeTextSearch] = { value: freeTextSearch };
        }
        
        // role
        if (role) {
            filterRequestObj[SystemUserFilterSchema.role] = { value: role };
        }
        // Creation date range
        if (creationFromDate || creationToDate) {
            filterRequestObj[SystemUserFilterSchema.creationDate] = {
                value: {
                    from: creationFromDate ? creationFromDate.getTime() : null,
                    to: creationToDate ? creationToDate.getTime() : null
                }
            };
        }
        // Modification date range
        if (modificationFromDate || modificationToDate) {
            filterRequestObj[SystemUserFilterSchema.modificationDate] = {
                value: {
                    from: modificationFromDate ? modificationFromDate.getTime() : null,
                    to: modificationToDate ? modificationToDate.getTime() : null
                }
            };
        }
        
        onFilterChange(filterRequestObj);
    }, [
        freeTextSearch, onFilterChange, role,
        creationFromDate, creationToDate,
        modificationFromDate, modificationToDate
    ]);

    useEffect(() => {
        (async () => {
            try {
                const sessionDataResp = await Api.getSessionData({
                    attributes: [SessionDataGetSchema.availableSystemRoles]
                });
                setAvailableRoles(sessionDataResp.data.availableSystemRoles);
            } catch (e) {
                console.error('SystemUserFilterPane: error reading availableSystemRoles', e);
                setPageError(true);
            }
            setPageLoaded(true);
        })();
    }, []);


    return (
        <FilterLayout clearAllAction={resetToDefaults} loading={!pageLoaded}>
            {/* Role tab */}
            <SMCExpansionPanel title={t('SMC_USER_ROLE')}>
                <RowLayout spacing={5}>
                    <SMCSelect
                        value={role}
                        onChange={e => setRole(e.target.value)}
                        inline
                        width="150px"
                    >
                        <SMCOption value="" />
                        {
                            availableRoles && availableRoles.map(roleName => (
                                <SMCOption key={roleName} value={roleName}>{roleName}</SMCOption>
                            ))
                        }
                    </SMCSelect>
                    <SMCIcon icon="clear" onClick={() => setRole('')} />
                </RowLayout>
            </SMCExpansionPanel>
            {/* Creation tab */}
            <SMCExpansionPanel title={t('SMC_OBJECT_CREATETIME')}>
                <DateRangeChooser
                    fromDate={creationFromDate}
                    toDate={creationToDate}
                    setFromDate={setCreationFromDate}
                    setToDate={setCreationToDate}
                />
            </SMCExpansionPanel>
            {/* Modification tab */}
            <SMCExpansionPanel title={t('SMC_OBJECT_MODIFYTIME')}>
                <DateRangeChooser
                    fromDate={modificationFromDate}
                    toDate={modificationToDate}
                    setFromDate={setModificationFromDate}
                    setToDate={setModificationToDate}
                />
            </SMCExpansionPanel>
        </FilterLayout>
    );
}

export default React.memo(SystemUserFilterPane);